import { useMemo } from 'react';

import type { TimeRange } from '~/components/TimeRangeSelector/TimeRangeSelector';

const INTERVAL_TO_API_MAP: Record<string, string> = {
  '5m': '5 Minutes',
  '15m': '15 Minutes',
  '1h': '1 Hour',
  '1d': '1 Day',
  'custom-range': 'specific'
};

const URL_TO_TIME_MAP: Record<string, string> = {
  '5 Minutes': '5m',
  '15 Minutes': '15m',
  '1 Hour': '1h',
  '1 Day': '1d',
  specific: 'custom-range'
};

const TIME_OPTIONS = [
  { label: 'Last 5 minutes', value: '5m' },
  { label: 'Last 15 minutes', value: '15m' },
  { label: 'Last 1 hour', value: '1h' },
  { label: 'Last 1 day', value: '1d' }
];

type UseTimeRangeCalculationParams = {
  time: string;
  dateFrom: string | null;
  dateTo: string | null;
};

export function useTimeRangeCalculation({
  time,
  dateFrom,
  dateTo
}: UseTimeRangeCalculationParams) {
  const effectiveTime = time || '5 Minutes';

  const effectiveDateFrom = useMemo(() => {
    if (dateFrom) return dateFrom;
    const date = new Date();
    date.setMinutes(date.getMinutes() - 5);
    return date.toISOString();
  }, [dateFrom]);

  const effectiveDateTo = useMemo(() => {
    if (dateTo) return dateTo;
    return new Date().toISOString();
  }, [dateTo]);

  const specificDates = useMemo(() => {
    if (
      effectiveTime !== 'specific' ||
      !effectiveDateFrom ||
      !effectiveDateTo
    ) {
      return null;
    }

    const fromDateObj = new Date(effectiveDateFrom);
    fromDateObj.setSeconds(0, 0);

    const toDateObj = new Date(effectiveDateTo);
    toDateObj.setSeconds(59, 999);

    return { dateFrom: fromDateObj, dateTo: toDateObj };
  }, [effectiveTime, effectiveDateFrom, effectiveDateTo]);

  const internalTime = useMemo(
    () => URL_TO_TIME_MAP[effectiveTime] || '5m',
    [effectiveTime]
  );

  const apiTime = useMemo(
    () => INTERVAL_TO_API_MAP[internalTime] || '5 Minutes',
    [internalTime]
  );

  const currentTimeRange = useMemo<TimeRange>(() => {
    if (internalTime === 'custom-range' && dateFrom && dateTo) {
      return {
        interval: 'custom-range',
        label: 'Custom Range',
        name: 'custom-range',
        dateFrom,
        dateTo
      };
    }

    const timeOption = TIME_OPTIONS.find(opt => opt.value === internalTime);
    const selectedTime = internalTime || '5m';

    return {
      interval: selectedTime,
      label: timeOption?.label || TIME_OPTIONS[0].label,
      name: selectedTime,
      dateFrom: '',
      dateTo: ''
    };
  }, [internalTime, dateFrom, dateTo]);

  return {
    apiTime,
    specificDates,
    currentTimeRange
  };
}
